import { CancelActiveInvocationsResponse } from "@/shared/domain/actions";
import { AISummary } from "@/shared/domain/ai_summary.ts";
import { EventRecord } from "@/shared/domain/events";
import {
  HttpClient,
  type PaginatedResponse,
  robotoHeaders,
  RobotoNotFoundException,
} from "@/shared/http";
import { parseTimestampsAsBigInt } from "@/shared/json";

import { FileRecord } from "../files/FileRecord";

import {
  CreateDatasetRequest,
  DatasetRecord,
  UpdateDatasetRequest,
} from "./DatasetRecord";

interface Options {
  abortSignal: AbortSignal;
  pageToken: string;
  limit: number;
  resourceOwnerId: string;
  searchParams: URLSearchParams;
}

interface ListFilesOptions {
  abortSignal: AbortSignal;
  pageToken: string;
  includePatterns: string[];
  excludePatterns: string[];
  limit: number;
  sortBy: string;
  sortDirection: "ASC" | "DESC";
}

export class DatasetService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createDataset(
    request: CreateDatasetRequest,
    options: { resourceOwnerId: string; abortSignal?: AbortSignal },
  ): Promise<DatasetRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/datasets");

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(request),
      signal: options.abortSignal,
    });

    return response.json<DatasetRecord>();
  }

  public async cancelAssociatedInvocations(
    datasetId: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<CancelActiveInvocationsResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/invocations/cancel`,
    );

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    return response.json<CancelActiveInvocationsResponse>();
  }

  public async getEventsForDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<EventRecord>> {
    const searchParams = new URLSearchParams();

    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }

    if (options?.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const url = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/events`,
      searchParams,
    );

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });

    return response.json<PaginatedResponse<EventRecord>>(
      parseTimestampsAsBigInt,
    );
  }

  public async getTagsForOrg(options?: Partial<Options>): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error("getTagsForOrg requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl("v1/datasets/tags");
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return response.json<string[]>();
  }

  public async getMetadataKeysForOrg(
    options?: Partial<Options>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "getMetadataKeysForOrg requires an Org ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/datasets/metadata/keys",
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return response.json<string[]>();
  }

  public async deleteDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.resourceOwnerId) {
      throw Error("deleteDataset requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: false,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    await response.throwIfError();
  }

  public async getDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<DatasetRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.json<DatasetRecord>();
  }

  public async updateDataset(
    datasetId: string,
    updates: UpdateDatasetRequest,
    options?: Partial<Options>,
  ): Promise<DatasetRecord> {
    if (!options?.resourceOwnerId) {
      throw Error("updateDataset requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}`,
    );

    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return response.json<DatasetRecord>();
  }

  /**
   * Fetch the dataset's existing AI summary, or `null` if none has been
   * generated yet.
   *
   * The service returns 404 when no summary exists — generation is never an
   * implicit side effect of reading. Callers surface `null` as a "Generate
   * summary" affordance and kick off generation explicitly via
   * {@link generateSummary}.
   */
  public async getSummary(
    datasetId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<AISummary | null> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/summary`,
    );
    try {
      const response = await this.#httpClient.get(requestUrl, {
        signal: options?.abortSignal,
        headers: robotoHeaders({}),
      });
      // Keep the `await`: the 404 for a missing summary is raised while reading
      // the *body*, so the rejection comes from `json()`, not `get()`. A bare
      // `return response.json()` would hand the rejected promise back after this
      // `try` has already exited, skipping the `catch` and propagating the 404
      // instead of mapping it to `null` — leaving the UI stuck on an error
      // instead of "Generate summary".
      return await response.json<AISummary>();
    } catch (error) {
      // A missing summary surfaces as a plain RobotoNotFoundException; a missing
      // *dataset* surfaces as the RobotoDatasetNotFoundException subclass (also a
      // 404). Only the former means "no summary yet" — match the exact class via
      // `name` so a missing dataset propagates instead of rendering the empty
      // "Generate summary" state.
      if (
        error instanceof RobotoNotFoundException &&
        error.name === "RobotoNotFoundException"
      ) {
        return null;
      }
      throw error;
    }
  }

  public async generateSummary(
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<AISummary> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/summary`,
    );

    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({}),
    });
    return response.json<AISummary>();
  }

  /**
   * List files in a dataset with optional pattern-based filtering and sorting.
   *
   * Returns all files in the dataset that match the specified include patterns
   * while excluding those that match exclude patterns. Uses gitignore-style
   * pattern matching for flexible file selection.
   *
   * Files are sorted by creation date (newest first) by default.
   *
   * @param datasetId - The ID of the dataset to list files from
   * @param options - Optional parameters for filtering, sorting, and pagination
   * @param options.includePatterns - List of gitignore-style patterns for files to include.
   *                                  If undefined, all files are considered.
   * @param options.excludePatterns - List of gitignore-style patterns for files to exclude.
   *                                  Takes precedence over include patterns.
   * @param options.pageToken - Token for retrieving the next page of results
   * @param options.limit - Maximum number of files to return per page
   * @param options.sortBy - Field to sort by (default: "created")
   * @param options.sortDirection - Sort direction "ASC" or "DESC" (default: "DESC")
   * @param options.abortSignal - Signal to abort the request
   * @returns A paginated response containing file records
   *
   * @example
   * ```typescript
   * // List all files (newest first by default)
   * const allFiles = await datasetService.listFiles("ds_abc123");
   *
   * // List only image files, excluding back camera
   * const imageFiles = await datasetService.listFiles("ds_abc123", {
   *   includePatterns: ["**\/*.png", "**\/*.jpg"],
   *   excludePatterns: ["**\/back_camera\/**"]
   * });
   *
   * // List files sorted by name
   * const sortedFiles = await datasetService.listFiles("ds_abc123", {
   *   sortBy: "name",
   *   sortDirection: "ASC"
   * });
   * ```
   */
  public async listFiles(
    datasetId: string,
    options?: Partial<ListFilesOptions>,
  ): Promise<PaginatedResponse<FileRecord>> {
    const requestBody: {
      page_token?: string;
      include_patterns?: string[];
      exclude_patterns?: string[];
      limit?: number;
      sort_by?: string;
      sort_direction?: "ASC" | "DESC";
    } = {};

    if (options?.pageToken !== undefined) {
      requestBody.page_token = options.pageToken;
    }

    if (options?.includePatterns !== undefined) {
      requestBody.include_patterns = options.includePatterns;
    }

    if (options?.excludePatterns !== undefined) {
      requestBody.exclude_patterns = options.excludePatterns;
    }

    if (options?.limit !== undefined) {
      requestBody.limit = options.limit;
    }

    if (options?.sortBy !== undefined) {
      requestBody.sort_by = options.sortBy;
    }

    if (options?.sortDirection !== undefined) {
      requestBody.sort_direction = options.sortDirection;
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/files/query`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({}),
      body: JSON.stringify(requestBody),
      idempotent: true,
    });
    return response.json<PaginatedResponse<FileRecord>>();
  }

  /**
   * List all files in a dataset with optional pattern-based filtering.
   *
   * Automatically handles pagination and returns all file records that match
   * the specified patterns. This method will continue fetching pages until
   * all files have been retrieved.
   *
   * Uses the listFiles() method internally.
   */
  public async listAllFiles(
    datasetId: string,
    options?: Partial<Omit<ListFilesOptions, "pageToken">>,
  ): Promise<FileRecord[]> {
    const allFiles: FileRecord[] = [];
    let pageToken: string | undefined = undefined;

    while (true) {
      const paginatedResponse = await this.listFiles(datasetId, {
        ...options,
        pageToken,
      });

      allFiles.push(...paginatedResponse.items);

      if (paginatedResponse.next_token) {
        pageToken = paginatedResponse.next_token;
      } else {
        break;
      }
    }

    return allFiles;
  }
}
